import React, { useState, useCallback, useEffect } from 'react';
import { apiFetch } from '../api/api';
import './AttachmentManager.css';

const AttachmentManager = ({
    parentRecord,
    parentType,
    parentIdField,
    onAttachmentChange = null, // Make optional
    allowedFileTypes = ['image/*', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'],
    maxFileSize = 10 * 1024 * 1024, // 10MB default
    className = ''
}) => {
    const [attachments, setAttachments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showPreview, setShowPreview] = useState(null);

    // Load attachments when parent record changes
    useEffect(() => {
        const loadAttachments = async () => {
            if (!parentRecord || !parentRecord[parentIdField]) {
                setAttachments([]);
                if (onAttachmentChange) {
                    onAttachmentChange([]);
                }
                return;
            }

            setIsLoading(true);
            try {
                const response = await apiFetch('/api/attachments');
                
                // Filter attachments based on the parent record
                let parentAttachments = [];
                const parentId = parentRecord[parentIdField];
                const wellIdRaw = parentRecord.WellID || parentRecord.wellId;
                
                // Validate WellID for filtering
                const wellId = parseInt(wellIdRaw, 10);
                
                console.log(`🔍 Loading attachments - WellID validation:`, {
                    parentType,
                    parentId,
                    wellIdRaw,
                    wellIdParsed: wellId,
                    isValidNumber: !isNaN(wellId),
                    isPositive: wellId > 0
                });
                
                if (!wellIdRaw || isNaN(wellId) || wellId <= 0) {
                    console.warn(`⚠️ Invalid WellID (${wellIdRaw}) for loading ${parentType} attachments. No attachments will be loaded.`);
                    parentAttachments = [];
                } else if (parentType === 'BHA' || parentType === 'Casing' || parentType === 'Deviation') {
                    // Filter by specific parent record ID in subject/description
                    const parentIdPattern = `${parentIdField}:${parentId}`;
                    
                    parentAttachments = response.filter(att => 
                        att.WellID === wellId &&
                        (att.Subject?.includes(parentIdPattern) || 
                         att.Description?.includes(`${parentIdField}: ${parentId}`))
                    );
                    
                    console.log(`🔍 Filtering attachments for ${parentType} ${parentIdField}:${parentId}`, {
                        wellId,
                        totalAttachments: response.length,
                        filteredAttachments: parentAttachments.length,
                        pattern: parentIdPattern
                    });
                } else {
                    // Generic fallback
                    parentAttachments = response.filter(att => 
                        att.WellID === wellId
                    );
                }
                
                setAttachments(parentAttachments);
                
                if (onAttachmentChange) {
                    onAttachmentChange(parentAttachments);
                }
            } catch (error) {
                console.error('Failed to load attachments:', error);
                setAttachments([]);
                if (onAttachmentChange) {
                    onAttachmentChange([]);
                }
            } finally {
                setIsLoading(false);
            }
        };

        loadAttachments();
    }, [parentRecord?.[parentIdField], parentType]); // Only depend on the actual ID value and type

    // Separate function for reloading after upload/delete  
    const reloadAttachments = useCallback(async () => {
        if (!parentRecord || !parentRecord[parentIdField]) return;

        try {
            const response = await apiFetch('/api/attachments');
            
            // Filter attachments based on the parent record
            let parentAttachments = [];
            const parentId = parentRecord[parentIdField];
            const wellIdRaw = parentRecord.WellID || parentRecord.wellId;
            
            // Validate WellID for filtering (reload)
            const wellId = parseInt(wellIdRaw, 10);
            
            if (!wellIdRaw || isNaN(wellId) || wellId <= 0) {
                console.warn(`⚠️ Invalid WellID (${wellIdRaw}) for reloading ${parentType} attachments. No attachments will be loaded.`);
                parentAttachments = [];
            } else if (parentType === 'BHA' || parentType === 'Casing' || parentType === 'Deviation') {
                // Filter by specific parent record ID in subject/description
                const parentIdPattern = `${parentIdField}:${parentId}`;
                
                parentAttachments = response.filter(att => 
                    att.WellID === wellId &&
                    (att.Subject?.includes(parentIdPattern) || 
                     att.Description?.includes(`${parentIdField}: ${parentId}`))
                );
            } else {
                // Generic fallback
                parentAttachments = response.filter(att => 
                    att.WellID === wellId
                );
            }
            
            setAttachments(parentAttachments);
            
            if (onAttachmentChange) {
                onAttachmentChange(parentAttachments);
            }
        } catch (error) {
            console.error('Failed to reload attachments:', error);
        }
    }, [parentRecord?.[parentIdField], parentType, onAttachmentChange]);

    // Upload file
    const uploadFile = useCallback(async (file) => {
        if (!parentRecord || !parentRecord[parentIdField]) {
            alert(`Please select a ${parentType} first`);
            return;
        }

        // Check file size
        if (file.size > maxFileSize) {
            alert(`File size too large. Maximum size is ${Math.round(maxFileSize / (1024 * 1024))}MB`);
            return;
        }

        console.log(`📤 Uploading file: ${file.name} for ${parentType}: ${parentRecord[parentIdField]}`);
        setIsUploading(true);
        setUploadProgress(0);

        try {
            const formData = new FormData();
            formData.append('file', file);
            
            // Ensure all fields are properly set and not empty
            // Include parent record ID in subject for precise filtering
            const parentId = parentRecord[parentIdField];
            const subject = `${parentType} ${parentIdField}:${parentId}`;
            const description = `${parentType} attachment for ${parentIdField}: ${parentId}`;
            const category = parentType;
            const relatedId = parentId.toString();
            
            formData.append('subject', subject);
            formData.append('description', description);
            
            // Map parentType to appropriate backend field
            // For now, link everything to wellId since most entities belong to a well
            if (parentType === 'BHA' || parentType === 'Casing' || parentType === 'Deviation') {
                // For these types, we need to get the WellID from the parent record
                const wellIdRaw = parentRecord.WellID || parentRecord.wellId;
                
                // Validate WellID is a valid positive integer
                const wellId = parseInt(wellIdRaw, 10);
                
                console.log('🔍 WellID validation:', {
                    parentType,
                    parentRecord,
                    wellIdRaw,
                    wellIdParsed: wellId,
                    isValidNumber: !isNaN(wellId),
                    isPositive: wellId > 0
                });
                
                if (!wellIdRaw || isNaN(wellId) || wellId <= 0) {
                    console.error('❌ Invalid WellID detected:', {
                        rawValue: wellIdRaw,
                        parsedValue: wellId,
                        parentType,
                        parentRecordKeys: Object.keys(parentRecord || {})
                    });
                    throw new Error(`Invalid WellID (${wellIdRaw}) found in ${parentType} record. Please select a valid well first.`);
                }
                
                formData.append('wellId', wellId.toString());
            } else {
                // Generic fallback - try to link to wellId
                const wellId = parseInt(relatedId, 10);
                if (!relatedId || isNaN(wellId) || wellId <= 0) {
                    throw new Error(`Invalid WellID (${relatedId}) for generic ${parentType} record`);
                }
                formData.append('wellId', wellId.toString());
            }

            // Debug: Log what we're sending
            console.log('📤 Upload details:', {
                fileName: file.name,
                fileSize: file.size,
                parentType,
                parentId: parentRecord[parentIdField],
                subject,
                description,
                category,
                relatedId
            });

            console.log('📤 FormData contents:');
            for (let [key, value] of formData.entries()) {
                if (key === 'file') {
                    console.log(`${key}:`, `File: ${value.name} (${value.size} bytes)`);
                } else {
                    console.log(`${key}:`, value);
                }
            }

            const response = await apiFetch('/api/attachments', {
                method: 'POST',
                body: formData
            });

            console.log('✅ File uploaded successfully:', response);
            
            // Reload attachments
            await reloadAttachments();
            
        } catch (error) {
            console.error('❌ File upload failed:', error);
            alert('Failed to upload file: ' + error.message);
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    }, [parentRecord, parentIdField, parentType, maxFileSize, reloadAttachments]);

    // Delete attachment
    const deleteAttachment = useCallback(async (attachment) => {
        if (!window.confirm(`Are you sure you want to delete "${attachment.FileName}"?`)) {
            return;
        }

        try {
            await apiFetch(`/api/attachments/${attachment.AttachmentID}`, {
                method: 'DELETE'
            });
            
            console.log('✅ Attachment deleted successfully');
            await reloadAttachments();
            
        } catch (error) {
            console.error('❌ Attachment deletion failed:', error);
            alert('Failed to delete attachment: ' + error.message);
        }
    }, [reloadAttachments]);

    // Drag and drop handlers
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (!parentRecord) {
            alert(`Please select a ${parentType} first`);
            return;
        }

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            files.forEach(file => uploadFile(file));
        }
    }, [parentRecord, parentType, uploadFile]);

    // File input handler
    const handleFileSelect = useCallback((e) => {
        if (!parentRecord) {
            alert(`Please select a ${parentType} first`);
            e.target.value = '';
            return;
        }

        const files = Array.from(e.target.files);
        files.forEach(file => uploadFile(file));
        e.target.value = '';
    }, [parentRecord, parentType, uploadFile]);

    // Get file type icon
    const getFileIcon = useCallback((fileName) => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return '📄';
            case 'doc':
            case 'docx':
                return '📝';
            case 'xls':
            case 'xlsx':
                return '📊';
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'bmp':
                return '🖼️';
            case 'txt':
                return '📄';
            case 'zip':
            case 'rar':
                return '📦';
            default:
                return '📎';
        }
    }, []);

    // Check if file is image
    const isImageFile = useCallback((fileName) => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension);
    }, []);

    // Format file size
    const formatFileSize = useCallback((bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }, []);

    if (!parentRecord) {
        return (
            <div className={`attachment-manager ${className}`}>
                <div className="attachment-placeholder">
                    <div className="placeholder-icon">📎</div>
                    <p>Select a {parentType} to manage attachments</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`attachment-manager ${className}`}>
            <div className="attachment-header">
                <h3>📎 Attachments</h3>
                <div className="attachment-controls">
                    <input
                        type="file"
                        id="attachment-file-input"
                        accept={allowedFileTypes.join(',')}
                        onChange={handleFileSelect}
                        multiple
                        style={{ display: 'none' }}
                    />
                    <button 
                        className="upload-btn"
                        onClick={() => document.getElementById('attachment-file-input').click()}
                        disabled={isUploading || !parentRecord}
                        title={!parentRecord ? `Select a ${parentType} first` : "Browse for files"}
                    >
                        📁 Add Files
                    </button>
                </div>
            </div>

            <div 
                className={`attachment-drop-zone ${isDragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {isUploading && (
                    <div className="upload-progress">
                        <div className="spinner"></div>
                        <p>Uploading...</p>
                        {uploadProgress > 0 && (
                            <div className="progress-bar">
                                <div 
                                    className="progress-fill" 
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                        )}
                    </div>
                )}

                {isDragOver && (
                    <div className="drag-overlay">
                        <div className="drag-message">
                            <div className="drag-icon">📤</div>
                            <p>Drop files here to upload</p>
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <div className="loading-indicator">
                        <div className="spinner"></div>
                        <p>Loading attachments...</p>
                    </div>
                ) : attachments.length > 0 ? (
                    <div className="attachments-list">
                        {attachments.map(attachment => (
                            <div key={attachment.AttachmentID} className="attachment-item">
                                <div className="attachment-icon">
                                    {getFileIcon(attachment.FileName)}
                                </div>
                                <div className="attachment-info">
                                    <div className="attachment-name" title={attachment.FileName}>
                                        {attachment.FileName}
                                    </div>
                                    <div className="attachment-meta">
                                        {attachment.FileSize && (
                                            <span className="file-size">
                                                {formatFileSize(attachment.FileSize)}
                                            </span>
                                        )}
                                        {attachment.CreatedDate && (
                                            <span className="upload-date">
                                                {new Date(attachment.CreatedDate).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="attachment-actions">
                                    {isImageFile(attachment.FileName) && (
                                        <button
                                            className="preview-btn"
                                            onClick={() => setShowPreview(attachment)}
                                            title="Preview"
                                        >
                                            🔍
                                        </button>
                                    )}
                                    <a
                                        href={`/uploads/${attachment.FileName}`}
                                        download={attachment.FileName}
                                        className="download-btn"
                                        title="Download"
                                    >
                                        ⬇️
                                    </a>
                                    <button
                                        className="delete-btn"
                                        onClick={() => deleteAttachment(attachment)}
                                        title="Delete"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="no-attachments">
                        <div className="no-attachments-icon">📎</div>
                        <p>No attachments found</p>
                        <div className="drop-instructions">
                            <p><strong>Add attachments:</strong></p>
                            <ul>
                                <li>📁 Click "Add Files" to browse</li>
                                <li>🖱️ Drag & drop files here</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            {/* Image Preview Modal */}
            {showPreview && isImageFile(showPreview.FileName) && (
                <div className="modal-overlay" onClick={() => setShowPreview(null)}>
                    <div className="image-preview-modal" onClick={e => e.stopPropagation()}>
                        <div className="preview-header">
                            <h3>File Preview</h3>
                            <p className="file-info">{showPreview.FileName}</p>
                            <button 
                                className="close-btn"
                                onClick={() => setShowPreview(null)}
                            >
                                ✕
                            </button>
                        </div>
                        <div className="preview-content">
                            <img 
                                src={`/uploads/${showPreview.FileName}`}
                                alt={showPreview.FileName}
                                className="preview-image"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AttachmentManager; 